import { getSessionData } from '@/auth/authUtil';
import Table from '@/components/Table';
import { knowledgeIntro } from '@/config';
import { useUpload } from '@/hooks/useUpload';
import { ModalPageRef, RootState } from '@/typings';
import { formatNumber } from '@/util';
import {
	ElButton,
	ElIcon,
	ElInput,
	ElLink,
	ElLoading,
	ElMessage,
	ElPopconfirm,
	ElProgress,
	ElScrollbar,
	ElTableColumn,
	ElTag,
	ElUpload,
	PaginationRef,
	RenderCell,
} from '@qsxy/element-plus-react';
import { useRequest } from 'ahooks';
import dayjs from 'dayjs';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { delDocument, knowledgeList } from './api';
import FilePreview from './FilePreview';
import Guide from './guide';
import './style.scss';
import { useLocation } from 'react-router-dom';

const Knowledge = () => {
	const loginUser = useSelector<RootState, RootState['user']>(state => state.user ?? {});

	const location = useLocation();
	// const versionUsage = useSelector<RootState, RootState['versionUsage']>(state => state.versionUsage ?? {});
	const { accept, beforeUpload, onUpload } = useUpload();
	const pageRef = useRef<PaginationRef>(null);
	const filePreviewRef = useRef<ModalPageRef<any>>(null);

	const [count, setCount] = useState({
		fileSize: 0,
		total: 0,
	});
	const [visible, setVisible] = useState(false);

	const refreshSize = () => {
		const { usage } = getSessionData();
		if (usage?.fileSize && usage?.totalFileSize) {
			setCount({
				fileSize: usage.fileSize,
				total: usage.totalFileSize,
			});
		}
	};

	const {
		data: { content: tableData = [], totalPages = 0 } = {},
		loading,
		run,
	} = useRequest(
		params => {
			const { user } = getSessionData();
			return knowledgeList({
				pageNo: pageRef.current.pageNum,
				pageSize: 10,
				createdBy: user.username,
				datasetId: user.datasetId,
				...params,
			});
		},
		{
			manual: true,
			onSuccess() {
				refreshSize();
			},
		},
	);

	/** 查询方法 */
	const onQuery = useCallback(
		(p: { pageNum?: number; name?: string } = {}) => {
			run(p);
		},
		[run],
	);

	/** 批量删除 */
	const handleDel = useCallback(
		(row: any) => {
			const load = ElLoading.service({ text: '正在删除中，请稍候...' });
			const { user } = getSessionData();
			delDocument({ dataset_id: user.datasetId, ...row })
				.then(result => {
					load.close();
					if (result) {
						onQuery();
						ElMessage.success('删除成功！');
					} else {
						ElMessage.error('删除失败！');
					}
				})
				.catch(() => load.close());
		},
		[onQuery],
	);

	useEffect(() => {
		if (loginUser?.username && !loginUser?.isVisitor && loginUser.datasetId) {
			onQuery();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loginUser]);

	useEffect(refreshSize, [location]);

	return (
		<>
			<ElScrollbar>
				<div className="ttn-knowledge">
					<div className="title">
						<span>个人知识库</span>
						{!loginUser?.isVisitor && !!loginUser.datasetId ? (
							<ElLink type="primary" onClick={() => setVisible(true)}>
								<ElIcon name="circle-location-arrow" prefix="fas" style={{ marginRight: 5 }} /> 个人知识库引导
							</ElLink>
						) : null}
					</div>
					<div className="introduction">{knowledgeIntro}</div>
					{loginUser?.isVisitor || visible || !loginUser.datasetId ? (
						<Guide callback={() => setVisible(false)}></Guide>
					) : (
						<>
							<div className="storage">
								<div className="space">
									<ElIcon name="cloud" prefix="far" />
									<span className="rest">剩余 {formatNumber(count.fileSize / 1024 / 1024, 2)} MB</span>
									<span>(共 {formatNumber(count.total / 1024 / 1024, 2)} MB)</span>
								</div>
								<ElProgress percentage={((count.total / 1024 / 1024 - count.fileSize / 1024 / 1024) / 50) * 100} showText={false} strokeWidth={8} />
							</div>

							<ElUpload
								className="ttn-upload"
								action="#"
								autoUpload={false}
								accept={accept}
								beforeUpload={beforeUpload}
								drag
								showFileList={false}
								withCredentials
								onChange={uploadFile => onUpload(uploadFile, onQuery)}
							>
								<ElIcon className="el-icon--upload" name="cloud-arrow-up" prefix="far" />
								<ElButton type="info" icon="link" plain>
									点击或拖拽文件上传
								</ElButton>
								<div className="text">文件类型：PDF, Word, EXCEL</div>
								<div className="text">最大文件大小：15.00 MB</div>
							</ElUpload>

							<div className="ttn-table">
								<div className="search">
									<div className="title">全部文件</div>
									<ElInput
										prefix={<ElIcon name="magnifying-glass" />}
										placeholder="按名称搜索"
										onKeyUp={e => {
											if (e.key === 'Enter') {
												// @ts-ignore
												onQuery({ pageNum: 1, name: e.target.value });
											}
										}}
										onClear={() => onQuery({ pageNum: 1 })}
									/>
								</div>

								<Table<any> data={tableData} total={totalPages} loading={loading} pageRef={pageRef} onQuery={onQuery}>
									<ElTableColumn label="文件名" prop="name"></ElTableColumn>
									<ElTableColumn label="文件大小" prop="fileSize" width={120} align="center">
										{({ row }: RenderCell<any>) => {
											if (row.fileSize < 1024 * 1024) {
												return `${formatNumber(row.fileSize / 1024, 2)} KB`;
											} else {
												return `${formatNumber(row.fileSize / 1024 / 1024, 2)} MB`;
											}
										}}
									</ElTableColumn>
									<ElTableColumn
										label="上传日期"
										prop="createdAt"
										width={140}
										align="center"
										formatter={row => (row.createdAt ? dayjs(row.createdAt).format('YYYY-MM-DD') : '')}
									></ElTableColumn>
									<ElTableColumn label="实体" width={150} align="center">
										{({ row }: RenderCell<any>) => (
											<ElTag type="success" round disableTransitions={true}>
												<ElIcon name="check" />
												&nbsp;&nbsp; {row.field4}
											</ElTag>
										)}
									</ElTableColumn>
									<ElTableColumn label="表格" width={150} align="center">
										{({ row }: RenderCell<any>) => (
											<ElTag type="success" round disableTransitions={true}>
												<ElIcon name="check" />
												&nbsp;&nbsp; {row.field4}
											</ElTag>
										)}
									</ElTableColumn>
									<ElTableColumn label="图片" width={150} align="center">
										{({ row }: RenderCell<any>) => (
											<ElTag type="success" round disableTransitions={true}>
												<ElIcon name="check" />
												&nbsp;&nbsp; {row.field4}
											</ElTag>
										)}
									</ElTableColumn>
									<ElTableColumn label="操作" width={150} align="left">
										{({ row }) => (
											<div className="table-ope">
												<ElLink type="primary" onClick={() => filePreviewRef.current.open(row)}>
													查看
												</ElLink>
												{!row.readonly && (
													<ElPopconfirm title="确定要删除该行数据吗？" onConfirm={() => handleDel(row)}>
														<ElLink type="error">删除</ElLink>
													</ElPopconfirm>
												)}
											</div>
										)}
									</ElTableColumn>
								</Table>
							</div>
						</>
					)}
				</div>
			</ElScrollbar>

			<FilePreview ref={filePreviewRef} />
		</>
	);
};

export default Knowledge;
